import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Sparkles, Send, Loader2, User, Bot, RotateCcw } from 'lucide-react';
import { cn } from '@utils/cn';
import { useAIAssistant, type AIMessage } from '@hooks/useAIAssistant';

// ─── Chips de sugerencia según módulo ────────────────────────
const CHIPS_POR_MODULO: Record<string, string[]> = {
  ingresos:      ['¿Cómo autorizo un ingreso?', '¿Qué es ANTI_AUTOAUTH?', '¿Cómo filtro por período?'],
  egresos:       ['¿Cómo adjunto un comprobante?', '¿Cómo funciona el límite de autorización?', '¿Qué es un egreso recurrente?'],
  crm:           ['¿Cómo avanzo un prospecto?', '¿Qué significa APARTADO?', '¿Cómo creo una cotización?'],
  bancos:        ['¿Cómo concilio una cuenta?', '¿Qué es el flujo IB2?', '¿Cómo importo un estado de cuenta?'],
  proyectos:     ['¿Cómo funciona el mapa interactivo?', '¿Qué colores tiene cada estado?', '¿Cómo bloqueo el mapa?'],
  creditos:      ['¿Cómo calculo la mora?', '¿Qué es el scoring de socios?', '¿Diferencia entre crédito simple y francés?'],
  obra:          ['¿Qué es la Ley de Parkinson en obra?', '¿Cómo registro avance?', '¿Quién valida el avance?'],
  tareas:        ['¿Cómo creo una tarea?', '¿Cómo uso el chat interno?', '¿Qué hace el cron de tareas vencidas?'],
  postventa:     ['¿Cómo escalo un ticket a garantía?', '¿Cómo cierro un ticket?', '¿Cuándo se envía la encuesta?'],
  reportes:      ['¿Qué reportes están disponibles?', '¿Cómo programo un reporte?', '¿Quién puede ver reportes financieros?'],
  dashboard:     ['¿Qué KPIs muestra el dashboard?', '¿Cómo personalizo mis widgets?', '¿Con qué frecuencia se actualizan?'],
  configuracion: ['¿Cómo agrego un usuario?', '¿Cómo configuro límites de autorización?', '¿Cómo reabro un período?'],
  general:       ['¿Qué módulos tiene el ERP?', '¿Cómo funciona el flujo de autorización?', '¿Cómo accedo al portal cliente?'],
};

// ─── Burbuja de mensaje ───────────────────────────────────────
const MessageBubble: React.FC<{ msg: AIMessage }> = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={cn('flex gap-2', isUser && 'flex-row-reverse')}>
      <div
        className={cn(
          'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
          isUser ? 'bg-indigo-100' : 'bg-violet-100',
        )}
      >
        {isUser
          ? <User size={14} className="text-indigo-600" />
          : <Bot size={14} className="text-violet-600" />
        }
      </div>
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
          isUser
            ? 'bg-indigo-600 text-white rounded-tr-sm'
            : 'bg-gray-100 text-gray-800 rounded-tl-sm',
        )}
      >
        {msg.content}
        {msg.streaming && (
          <span className="inline-block w-1.5 h-4 bg-gray-400 ml-0.5 animate-pulse rounded-sm align-middle" />
        )}
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────
const AIAssistant: React.FC = () => {
  const { aiAssistantOpen, setAIAssistantOpen, moduloActivo, sendMessage, cancel } = useAIAssistant();

  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll al último mensaje
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus al abrir
  useEffect(() => {
    if (aiAssistantOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [aiAssistantOpen]);

  const closePanel = useCallback(() => {
    cancel();
    setLoading(false);
    setAIAssistantOpen(false);
  }, [cancel, setAIAssistantOpen]);

  // Esc para cerrar
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && aiAssistantOpen) closePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aiAssistantOpen, closePanel]);

  const chips = CHIPS_POR_MODULO[moduloActivo] ?? CHIPS_POR_MODULO['general'];

  const handleSend = useCallback(
    async (texto: string) => {
      const msg = texto.trim();
      if (!msg || loading) return;

      // ID estable generado una vez; capturado en todos los closures del stream
      const assistantId = Date.now();

      setInput('');
      setLoading(true);
      setMessages((prev) => [
        ...prev,
        { id: assistantId - 1, role: 'user',      content: msg, streaming: false },
        { id: assistantId,     role: 'assistant',  content: '',  streaming: true  },
      ]);

      let accumulated = '';
      await sendMessage(
        msg,
        (chunk) => {
          accumulated += chunk;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: accumulated, streaming: true }
                : m,
            ),
          );
        },
        () => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, streaming: false } : m,
            ),
          );
          setLoading(false);
        },
        (errMsg) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: `⚠️ ${errMsg}`, streaming: false }
                : m,
            ),
          );
          setLoading(false);
        },
      );
    },
    [loading, sendMessage],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  const resetConversation = () => {
    cancel();
    setMessages([]);
    setLoading(false);
    setInput('');
  };

  if (!aiAssistantOpen) return null;

  const showChips = messages.length === 0;

  return (
    <>
      {/* Backdrop semitransparente */}
      <div
        className="fixed inset-0 z-40 bg-black/10 backdrop-blur-[1px]"
        onClick={closePanel}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed bottom-4 right-4 z-50 w-[380px] max-h-[600px] flex flex-col',
          'bg-white rounded-2xl shadow-2xl border border-gray-200',
          'animate-in slide-in-from-bottom-4 duration-200',
        )}
        role="dialog"
        aria-label="Asistente IA"
      >
        {/* Header */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-100">
            <Sparkles size={16} className="text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 leading-none">Asistente RM</p>
            <p className="text-[11px] text-gray-400 mt-0.5 capitalize">{moduloActivo}</p>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={resetConversation}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Nueva conversación"
              >
                <RotateCcw size={14} />
              </button>
            )}
            <button
              onClick={() => { cancel(); setAIAssistantOpen(false); }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              title="Cerrar (Esc)"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Mensajes */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0" style={{ maxHeight: 400 }}>
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
              <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
                <Sparkles size={22} className="text-violet-500" />
              </div>
              <p className="text-sm font-medium text-gray-700">¿En qué te ayudo?</p>
              <p className="text-xs text-gray-400 max-w-[260px]">
                Pregúntame sobre el módulo de {moduloActivo} o sobre cualquier funcionalidad del ERP.
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <MessageBubble key={i} msg={msg} />
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Chips de sugerencia */}
        {showChips && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <button
                key={chip}
                onClick={() => handleSend(chip)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50 transition-colors"
              >
                {chip}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="px-3 pb-3 pt-2 border-t border-gray-100">
          <div className="flex items-end gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-200 focus-within:border-violet-400 transition-colors">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu pregunta… (Enter para enviar)"
              rows={1}
              disabled={loading}
              className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 resize-none outline-none leading-relaxed max-h-24 min-h-[20px]"
              style={{ overflowY: 'auto' }}
            />
            <button
              onClick={() => handleSend(input)}
              disabled={loading || !input.trim()}
              className={cn(
                'flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors',
                loading || !input.trim()
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-violet-600 text-white hover:bg-violet-700',
              )}
            >
              {loading
                ? <Loader2 size={13} className="animate-spin" />
                : <Send size={13} />
              }
            </button>
          </div>
          <p className="text-[10px] text-gray-400 text-center mt-1.5">
            Shift+Enter para nueva línea · Esc para cerrar
          </p>
        </div>
      </div>
    </>
  );
};

export default AIAssistant;
